// VF / VOSTFR. Les deux sources portent la langue à des endroits différents :
//
// - anime-sama la met dans le chemin de la saison ("saison1/vostfr",
//   "saison1/vf", parfois "vf1" quand il existe plusieurs doublages) ;
// - voir-anime en fait une fiche distincte, suffixée "(VF)" dans son titre, et
//   la répète dans le nom de chaque épisode ("293 VF").

export type Lang = "vostfr" | "vf";

export function langLabel(lang: Lang): string {
  return lang === "vf" ? "VF" : "VOSTFR";
}

export function isLang(value: string): value is Lang {
  return value === "vf" || value === "vostfr";
}

/** Réécrit un chemin de saison vers une autre langue.
 *
 *  anime-sama ne déclare que le VOSTFR dans ses `panneauAnime`, mais sert bien
 *  la VF à l'URL correspondante : « saison1/vostfr » a son « saison1/vf » sans
 *  que rien ne l'annonce. On construit donc l'URL, et c'est son contenu qui
 *  dira si cette langue existe — une combinaison absente rend une page 404, où
 *  aucun bloc `epsN` n'est trouvé. */
export function withLang(path: string, lang: Lang): string {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return path;

  const last = segments[segments.length - 1]!.toLowerCase();

  // on ne remplace que ce qui est bien un segment de langue
  if (last.startsWith("vostfr") || last.startsWith("vf")) segments[segments.length - 1] = lang;
  else segments.push(lang);

  return segments.join("/");
}

/** anime-sama : dernier segment du chemin de saison. null quand il ne dit
 *  rien de la langue — une telle saison ne doit jamais être masquée. */
export function seasonLang(path: string): Lang | null {
  const last = path.split("/").filter(Boolean).pop()?.toLowerCase() ?? "";

  if (last.startsWith("vostfr")) return "vostfr";
  if (last.startsWith("vf")) return "vf";

  return null;
}

/** voir-anime : le titre d'une fiche doublée porte "(VF)". Les autres sont en
 *  VOSTFR sans le dire, d'où l'absence de cas null ici. */
export function titleLang(name: string): Lang {
  return /\bVF\b|\(VF\)/i.test(name) ? "vf" : "vostfr";
}

/** voir-anime : nom d'épisode déjà nettoyé, de la forme "293 VF". */
export function episodeLang(name: string): Lang | null {
  if (/\bVOSTFR\b/i.test(name)) return "vostfr";
  if (/\bVF\b/i.test(name)) return "vf";

  return null;
}

export interface Filtered<T> {
  items: T[];
  /** Faux quand rien ne correspond : on montre alors tout plutôt qu'un écran
   *  vide, et l'appelant le signale. */
  matched: boolean;
}

export function filterLang<T>(
  items: T[],
  lang: Lang,
  langOf: (item: T) => Lang | null,
): Filtered<T> {
  const matched = items.some((item) => langOf(item) === lang);

  if (!matched) return { items, matched: false };

  return {
    items: items.filter((item) => {
      const own = langOf(item);
      return own === lang || own === null;
    }),
    matched: true,
  };
}
